import { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
    AlertTriangle,
    Calendar,
    Check,
    Edit,
    History,
    Trash2,
    X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { SHIFT_CHANGE_REQUEST } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { truncateText } from "../../components/common/CommonFunctions";
import {
    apiErrorMessage,
    canSubmitShiftChangeRequest,
    canViewMyShiftChangeRequests,
    requestTypeLabel,
    shiftRequestStatusLabel,
} from "./helpers/shiftChangeUtils";
import ShiftChangeRequestDialog from "./helpers/ShiftChangeRequestDialog";
import ShiftChangeReviewDialog from "./helpers/ShiftChangeReviewDialog";
import { SHIFT_CHANGE_STATUS_BADGE } from "./helpers/ShiftChangeReviewDialog";

const MyShiftChangeRequests = () => {
    const navigate = useNavigate();
    const { update } = useApiMethods();
    const canView = canViewMyShiftChangeRequests();
    const canSubmit = canSubmitShiftChangeRequest();

    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [loading, setLoading] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [inbox, setInbox] = useState("mine");

    const refresh = () => setServerTrigger((v) => !v);

    const openCreate = () => {
        setEditRecord(null);
        setCreateOpen(true);
    };

    const openEdit = (row) => {
        setEditRecord(row);
        setCreateOpen(true);
    };

    const openDetail = (row) => {
        setSelected(row);
        setDetailOpen(true);
    };

    const handleDelete = async () => {
        if (!deleteTarget?.id) return;
        try {
            setLoading(true);
            const res = await update(SHIFT_CHANGE_REQUEST, {
                id: deleteTarget.id,
                status: "cancelled",
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Request cancelled");
                setDeleteTarget(null);
                refresh();
            } else {
                ToastMsg("error", apiErrorMessage(res, "Failed to cancel request"));
            }
        } catch (e) {
            ToastMsg("error", apiErrorMessage(e, "Failed to cancel request"));
        } finally {
            setLoading(false);
        }
    };

    const handleCounterpartAction = async (record, action) => {
        if (!record?.id) return;
        try {
            setLoading(true);
            const res = await update(SHIFT_CHANGE_REQUEST, {
                id: record.id,
                counterpart_action: action,
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || (action === "accept" ? "Swap accepted" : "Swap declined"));
                refresh();
            } else {
                ToastMsg("error", apiErrorMessage(res, "Failed to update swap"));
            }
        } catch (e) {
            ToastMsg("error", apiErrorMessage(e, "Failed to update swap"));
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(() => [
        {
            accessor: "date",
            Header: "Date",
            sortable: true,
            Cell: ({ value }) => (value ? dayjs(value).format("DD MMM YYYY") : "-"),
        },
        {
            accessor: "request_type",
            Header: "Type",
            Cell: ({ value }) => requestTypeLabel(value),
        },
        {
            accessor: "current_shift_name",
            Header: "Current Shift",
            Cell: ({ value, row }) => {
                const record = row?.original ?? row;
                if (record.request_type === "swap" && record.my_role === "counterpart") {
                    return record.requested_shift_name || "—";
                }
                return value || "—";
            },
        },
        {
            accessor: "requested_shift_name",
            Header: "Requested Shift",
            Cell: ({ value, row }) => {
                const record = row?.original ?? row;
                if (record.request_type === "swap" && record.my_role === "counterpart") {
                    return record.current_shift_name || "—";
                }
                return value || "—";
            },
        },
        {
            accessor: "counterpart_name",
            Header: "Swap with",
            Cell: ({ value, row }) => {
                const record = row?.original ?? row;
                if (record.request_type !== "swap") return "—";
                if (record.my_role === "counterpart") return record.employee_name || "—";
                return value || "—";
            },
        },
        {
            accessor: "status",
            Header: "Status",
            Cell: ({ row }) => {
                const record = row?.original ?? row;
                const label = shiftRequestStatusLabel(record);
                return (
                    <span className={cn("px-2 py-0.5 rounded-full text-xs border capitalize", SHIFT_CHANGE_STATUS_BADGE[record.status] || SHIFT_CHANGE_STATUS_BADGE.pending)}>
                        {label}
                    </span>
                );
            },
        },
        { accessor: "reason", Header: "Reason", Cell: ({ value }) => truncateText(value, 36) },
        {
            accessor: "admin_comment",
            Header: "Review note",
            Cell: ({ value }) => truncateText(value, 32, "240px"),
        },
        {
            accessor: "requested_on",
            Header: "Submitted",
            Cell: ({ value }) => value || "-",
        },
        {
            Header: "Action",
            align: "right",
            fixed: "right",
            Cell: ({ row }) => {
                const record = row?.original ?? row;
                const isCounterpart = record.my_role === "counterpart" || Number(sessionStorage.getItem("employee_id") || 0) === Number(record.counterpart_id || 0);
                const waitingOnMe = record.request_type === "swap"
                    && record.status === "pending"
                    && record.counterpart_status === "pending"
                    && isCounterpart;

                if (waitingOnMe) {
                    return (
                        <div className="flex items-center justify-end gap-1">
                            <Button
                                size="sm"
                                className="h-8 bg-sidebar text-white hover:bg-sidebar-primary"
                                disabled={loading}
                                onClick={() => handleCounterpartAction(record, "accept")}
                            >
                                <Check className="h-3.5 w-3.5 mr-1" />
                                Accept
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-red-600 border-red-200"
                                disabled={loading}
                                onClick={() => handleCounterpartAction(record, "decline")}
                            >
                                <X className="h-3.5 w-3.5 mr-1" />
                                Decline
                            </Button>
                        </div>
                    );
                }

                if (record.status === "pending" && !isCounterpart) {
                    return (
                        <div className="flex items-center justify-end gap-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-md bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:text-blue-700"
                                title="Edit request"
                                onClick={() => openEdit(record)}
                            >
                                <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-md bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700"
                                title="Cancel request"
                                onClick={() => setDeleteTarget(record)}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    );
                }

                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="View request details"
                        onClick={() => openDetail(record)}
                    >
                        <History className="h-4 w-4 text-sidebar-primary" />
                    </Button>
                );
            },
        },
    ], [loading]);

    if (!canView) {
        return (
            <ContainerCard>
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Access denied</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md">
                        You do not have permission to view shift change requests. Ask your administrator to enable
                        Attendance → Shift Change Requests in User Permissions.
                    </p>
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Calendar}
                title="My Shift Change / Swap"
                subtitle="Change your own shift, or swap with a colleague for a date"
                totalRecords={totalRecords}
                buttonEnable={canSubmit}
                buttonName="New Request"
                setOpenDialog={openCreate}
            />

            <p className="text-xs text-muted-foreground mb-3">
                You can also raise a request from{" "}
                <button type="button" className="text-sidebar-primary hover:underline" onClick={() => navigate("/calendar")}>
                    Calendar
                </button>
                {" "}or{" "}
                <button type="button" className="text-sidebar-primary hover:underline" onClick={() => navigate("/home")}>
                    Home → Attendance
                </button>
                . Activity for each request appears under your profile Activity tab.
            </p>

            <div className="mb-3">
                <SegmentSwitch
                    value={inbox}
                    onChange={(value) => {
                        setInbox(value);
                        refresh();
                    }}
                    options={[
                        { value: "mine", label: "My requests" },
                        { value: "waiting", label: "Waiting for me" },
                    ]}
                />
            </div>

            <Reactable
                columns={columns}
                data={rows}
                setData={setRows}
                setTotalCount={setTotalRecords}
                url={SHIFT_CHANGE_REQUEST}
                serverSide
                serverTrigger={serverTrigger}
                filter={inbox === "waiting" ? { mine_only: true, inbox: "waiting" } : { mine_only: true }}
                rowSize="sm"
                isCSVRequired={false}
                isPDFRequired={false}
            />

            <ShiftChangeRequestDialog
                open={createOpen}
                onClose={() => {
                    setCreateOpen(false);
                    setEditRecord(null);
                }}
                onSuccess={refresh}
                initialDate={editRecord?.date || dayjs().format("YYYY-MM-DD")}
                lockDate={Boolean(editRecord)}
                currentShiftLabel={editRecord?.current_shift_name || null}
                hasAttendance
                editRecord={editRecord}
            />

            <ConfirmPopupModal
                open={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title={deleteTarget?.request_type === "swap" ? "Cancel shift swap request?" : "Cancel shift change request?"}
                description={`This will cancel your pending request for ${deleteTarget?.date ? dayjs(deleteTarget.date).format("DD MMM YYYY") : "this date"}.`}
                type="danger"
                confirmLabel="Cancel request"
                cancelLabel="Keep"
                loading={loading}
                destructive
            />

            <ShiftChangeReviewDialog
                open={detailOpen}
                onClose={() => {
                    setDetailOpen(false);
                    setSelected(null);
                }}
                selected={selected}
                readOnly
                hideEmployeeHeader
            />
        </ContainerCard>
    );
};

export default MyShiftChangeRequests;
